/**
 * BrokerCommissionViewPage — Broker Read-Only View
 *
 * Brokers see their own commission settings (read-only) and can tag their loans.
 * Does NOT show: Payment Type Visibility, Configuration History, Source Data Validation,
 * Broker filter dropdown, All Brokers overview, Forecast Impact Calculator, Save Settings.
 *
 * Route: /my-commission-settings
 * Access: Broker role, gated by allowedReports
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  UserCog,
  ChevronRight,
  Search,
  Tag,
  DollarSign,
  CreditCard,
  User,
  Building2,
  Briefcase,
  Loader2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import PageHeader from '../../components/common/PageHeader';
import { computeReconciliation } from '../../utils/drawAgainstCommission';
import { computeHurdleSchedule, hasRevenueHurdle } from '../../utils/revenueHurdle';
import { RevenueHurdleSchedule } from '../../components/brokers/RevenueHurdleSchedule';
import type { MonthlyReconciliation } from '../../utils/drawAgainstCommission';
import {
  buildFiscalYearGroups,
  currentFiscalYear,
  distinctLoanCount,
  fiscalYearLabel,
  fiscalYearToDateDetail,
  groupByFiscalYear,
  latestMonth,
  monthLabel,
  paymentRowKey,
  sumFiscalYear,
} from '../../utils/commissionTree';
import { useBrokerCommissionConfig } from '../../hooks/useBrokerCommissionConfig';
import { useBrokerLoanTags } from '../../hooks/useBrokerLoanTags';
import { useAuth } from '../../hooks/useAuth';
import { functions } from '../../lib/firebase';
import { shouldShowRecipientGrossColumn } from '../../utils/recipientCommissionPresentation';
import RecipientCommissionReadOnlyView from './RecipientCommissionReadOnlyView';
import { formatCurrency } from '../../utils/formatters';

// Page is gated by per-tenant `featureFlags.commission_settings === true` on
// the company doc. Activation is a runtime Firestore flip (no code deploy);
// replaces the prior hardcoded company-ID allowlist.

// ─── Types ───────────────────────────────────────────────────────────────────

interface LoanTagRow {
  loanId: string;
  customerName: string;
  lender: string;
  paymentType: 'upfront' | 'trail' | 'arrears' | 'discharged' | 'clawback' | 'referrer_deduction';
  /** Settled amount on upfront, current balance on trail. `null` when blank or
   *  the viewer has `hideLoanAmounts`; renders as an em dash, never $0. */
  loanValueCents: number | null;
  grossCommissionCents?: number;
  commissionCents: number;
  introductionType: 'broker' | 'company' | 'self_sourced' | null;
  lockedPct: number | null;
  lockedTrailPct?: number | null;
  brokerShareCents: number;
  effectiveRateBps: number | null;
  month: string;
  year: number;
  isUntagged: boolean;
  isArrears: boolean;
  isDischarged: boolean;
  /** A third-party referrer fee the COMPANY funds. Shown so the broker can see
   *  the referrer was paid; it never reduces their share. */
  isReferrerDeduction: boolean;
  isManualAdjustment?: boolean;
  manualAdjustmentId?: string;
  manualReason?: string;
}

// The loan tree's year > month > payment grouping lives in
// utils/commissionTree.ts (TreeYearGroup / TreeMonthGroup) and is shared with
// the admin page, so the two surfaces cannot drift apart.

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrencyWithCents(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Badge Components ────────────────────────────────────────────────────────

function PaymentTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    upfront: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    trail: 'bg-green-500/20 text-green-400 border-green-500/30',
    arrears: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    discharged: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    clawback: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    referrer_deduction: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  };
  const labels: Record<string, string> = { upfront: 'Upfront', trail: 'Trail', arrears: 'Arrears', discharged: 'Discharged', clawback: 'Clawback', referrer_deduction: 'Referrer Deduction' };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${styles[type] || 'bg-gray-600 text-gray-400 border-gray-500'}`}>
      {labels[type] || type}
    </span>
  );
}

/**
 * Can this row have an introduction split allocated against it?
 *
 * Mirrors `isTaggableRow` on BrokerCommissionSettingsPage. The two surfaces
 * render the same rows and MUST agree: without this, a REFERRER row showed the
 * broker an active introduction dropdown and a "$0.00" share on their own view
 * while the admin view showed a dash, implying a split it can never earn.
 */
function isTaggableRow(loan: LoanTagRow): boolean {
  return (
    !loan.isArrears &&
    !loan.isDischarged &&
    !loan.isReferrerDeduction &&
    !loan.isManualAdjustment
  );
}

function LoanStatusBadge({ loan }: { loan: LoanTagRow }) {
  if (loan.isManualAdjustment) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" title={loan.manualReason}>MANUAL</span>;
  // Above isUntagged: a row that carries no share is never awaiting a tag.
  if (loan.isReferrerDeduction) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30" title="Third-party referrer fee. Paid by the company, not deducted from your share.">REFERRER</span>;
  if (loan.isUntagged) return <span className="px-2 py-0.5 text-[0.625rem] font-bold rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">UNTAGGED</span>;
  if (loan.isDischarged) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30">DISCHARGED</span>;
  if (loan.isArrears) return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">ARREARS</span>;
  return <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />;
}

// ─── Stat Card ───────────────────────────────────────────────────────────────

const ACCENT_CLASSES: Record<string, string> = {
  blue: 'bg-gradient-to-r from-transparent via-blue-500 to-transparent',
  green: 'bg-gradient-to-r from-transparent via-green-500 to-transparent',
  purple: 'bg-gradient-to-r from-transparent via-purple-500 to-transparent',
  amber: 'bg-gradient-to-r from-transparent via-amber-500 to-transparent',
};

function StatCardWithIcon({ label, value, accent, detail, valueColor, icon, iconBg, iconColor }: {
  label: string; value: string; accent: string; detail: string; valueColor?: string;
  icon: React.ReactNode; iconBg: string; iconColor: string;
}) {
  return (
    <div className="rounded-lg shadow-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)' }}>
      <div className={`absolute top-0 left-0 right-0 h-[2px] ${ACCENT_CLASSES[accent] || ACCENT_CLASSES.blue}`} />
      <div className="p-4 md:p-5 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl md:text-3xl font-bold ${valueColor || 'text-white'}`}>{value}</p>
            <p className="text-xs text-gray-500 mt-1.5">{detail}</p>
          </div>
          <div className="p-2.5 rounded-xl" style={{ background: `linear-gradient(135deg, ${iconBg}, ${iconBg.replace('0.3', '0.1')})`, color: iconColor }}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Loading Spinner ─────────────────────────────────────────────────────────

function LoadingState({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      <p className="text-gray-400 text-sm">{message || 'Loading...'}</p>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const BrokerSelfCommissionViewPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<any>();
  const { brokerDocId: claimsBrokerDocId } = useAuth();
  const companyId = userData?.companyId as string | undefined;
  // Fallback to userData.brokerDocId if not in claims (claims may lag behind user doc)
  const brokerDocId = claimsBrokerDocId || userData?.brokerDocId || null;
  // Viewing user's uid — used to look up their own assistant share when the
  // role is broker_assistant. Falls back to userData.uid when useAuth's
  // underlying auth user isn't hydrated yet.

  const [refreshing, setRefreshing] = useState(false);

  // Loan tags state
  const [tagFilter, setTagFilter] = useState('all');
  const [tagSearch, setTagSearch] = useState('');
  const [loanSort, setLoanSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'loanId', dir: 'asc' });
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());
  const currentYear = new Date().getFullYear();
  const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  // One clock on this page: the tile, the reconciliation table and the loan
  // tree all group by Australian financial year (identified by its end year).
  const currentFY = currentFiscalYear();
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set([currentFY]));
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set([currentMonth]));

  // Reconciliation collapse state
  const [expandedReconYears, setExpandedReconYears] = useState<Set<number>>(new Set([currentFY]));

  // ─── Commission Config (real-time Firestore) ──────────────────────────────
  const {
    currentConfig,
    configHistory: rawConfigHistory,
    trailStartMonth,
    loading: configLoading,
    recipientView,
    recipientViewLoading,
    recipientViewError,
    refetchBigQueryData,
  } = useBrokerCommissionConfig(companyId, brokerDocId || undefined, {
    includeRecipientView: true,
  });

  const brokerIntroPct = currentConfig?.brokerIntroductionPct ?? 60;
  const companyIntroPct = currentConfig?.companyIntroductionPct ?? 40;
  // PR C — 3-tier read fields (only meaningful when threeTierActive). Defaults
  // mirror the admin settings page: trail* falls back to upfront pct on read,
  // self_sourced defaults to 0 (safe — no tags exist pre-activation).
  const trailBrokerIntroPct = currentConfig?.trailBrokerIntroductionPct ?? brokerIntroPct;
  const trailCompanyIntroPct = currentConfig?.trailCompanyIntroductionPct ?? companyIntroPct;
  const upfrontSelfSourcedPct = currentConfig?.upfrontSelfSourcedPct ?? 0;
  const trailSelfSourcedPct = currentConfig?.trailSelfSourcedPct ?? 0;

  // PR C — broker.commissionStructure flag drives the 3-tier UI. Subscribe
  // directly so the broker sees changes the moment the admin activates.
  const [threeTierActive, setThreeTierActive] = useState<boolean>(false);
  React.useEffect(() => {
    if (!companyId || !brokerDocId) {
      setThreeTierActive(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'companies', companyId, 'brokers', brokerDocId),
      (snap) => {
        const data = snap.data();
        setThreeTierActive(data?.commissionStructure === '3-tier');
      },
      () => setThreeTierActive(false),
    );
    return () => unsub();
  }, [companyId, brokerDocId]);

  // ─── Loan Tags (real-time Firestore) ───────────────────────────────────────
  const {
    tags: rawLoanTags,
    loading: tagsLoading,
    hasMore: tagsHasMore,
  } = useBrokerLoanTags(companyId, brokerDocId || undefined);

  // Build tag lookup map from Firestore tags
  const tagMap = useMemo(() => new Map(rawLoanTags.map(t => [t.loanId, { introductionType: t.introductionType, lockedPct: t.lockedPct }])), [rawLoanTags]);

  // The server response is the only source of displayed financial amounts.
  // Firestore tags remain live so brokers can keep classifying their loans.
  const recipientRows = useMemo(
    () => recipientView?.groups.flatMap((group) => group.rows) ?? [],
    [recipientView],
  );
  const recipientBqRows = useMemo(
    () => recipientRows.filter((row) => !row.isManualAdjustment),
    [recipientRows],
  );
  const recipientManualRows = useMemo(
    () => recipientRows.filter((row) => row.isManualAdjustment),
    [recipientRows],
  );

  // Merge BigQuery loans with Firestore tags — shows ALL loans, not just tagged ones
  const loanTags: LoanTagRow[] = useMemo(() => {
    let rows: LoanTagRow[];
    if (recipientBqRows.length > 0) {
      rows = recipientBqRows.map(recipientRow => {
        const tag = tagMap.get(recipientRow.loanId) || null;
        // Date-based default: historical loans (pre-2026-03) default to 'company',
        // new loans require explicit tagging
        // Firestore tags are live; prefer them over the one-time payout
        // snapshot so a successful tag write updates the row immediately.
        const introType = tag?.introductionType ?? recipientRow.introductionType;
        // Same one-predicate rule as the admin page: every row that carries no
        // share is handled identically, so the two surfaces cannot diverge.
        const isArrearsOrDischarged =
          recipientRow.isArrears ||
          recipientRow.isDischarged ||
          !!recipientRow.isReferrerDeduction;
        const [yearStr] = (recipientRow.periodMonth || currentMonth).split('-');
        return {
          loanId: recipientRow.loanId,
          customerName: recipientRow.customerName || 'Unknown',
          lender: recipientRow.lender || 'Unknown',
          paymentType: recipientRow.paymentType as LoanTagRow['paymentType'],
          loanValueCents: recipientRow.loanValueCents ?? null,
          grossCommissionCents: recipientRow.grossCommissionCents,
          commissionCents: recipientRow.grossCommissionCents ?? 0,
          introductionType: isArrearsOrDischarged ? null : introType,
          lockedPct: isArrearsOrDischarged ? null : (tag?.lockedPct ?? null),
          brokerShareCents: recipientRow.recipientCommissionCents,
          effectiveRateBps: recipientRow.effectiveRateBps,
          month: recipientRow.periodMonth || currentMonth,
          year: parseInt(yearStr, 10),
          isUntagged: isArrearsOrDischarged ? false : !introType,
          isArrears: recipientRow.isArrears,
          isDischarged: recipientRow.isDischarged,
          isReferrerDeduction: !!recipientRow.isReferrerDeduction,
        };
      });
    } else {
      rows = rawLoanTags.map(tag => {
        const isTagged = !!tag.introductionType;
        const updatedMonth = tag.updatedAt
          ? `${tag.updatedAt.getFullYear()}-${String(tag.updatedAt.getMonth() + 1).padStart(2, '0')}`
          : currentMonth;
        const [yearStr] = updatedMonth.split('-');
        return {
          loanId: tag.loanId,
          customerName: tag.customerName || 'Unknown',
          lender: tag.lender || 'Unknown',
          loanValueCents: null,
          paymentType: 'upfront' as const,
          commissionCents: 0,
          introductionType: isTagged ? tag.introductionType : null,
          lockedPct: tag.lockedPct ?? null,
          brokerShareCents: 0,
          effectiveRateBps: null,
          month: updatedMonth,
          year: parseInt(yearStr, 10),
          isUntagged: !isTagged,
          isArrears: false,
          isDischarged: false,
          isReferrerDeduction: false,
        };
      });
    }

    // Append manual adjustments as synthetic rows
    for (const recipientRow of recipientManualRows) {
      const [yearStr] = recipientRow.periodMonth.split('-');
      rows.push({
        loanId: recipientRow.loanId,
        customerName: recipientRow.customerName || 'Manual Adjustment',
        lender: recipientRow.lender || '—',
        // Authored against the ledger, not ingested: no loan value, never a
        // referrer fee.
        loanValueCents: null,
        paymentType: recipientRow.paymentType as LoanTagRow['paymentType'],
        grossCommissionCents: recipientRow.grossCommissionCents,
        commissionCents: recipientRow.grossCommissionCents ?? 0,
        introductionType: recipientRow.introductionType,
        lockedPct: null,
        brokerShareCents: recipientRow.recipientCommissionCents,
        effectiveRateBps: recipientRow.effectiveRateBps,
        month: recipientRow.periodMonth,
        year: parseInt(yearStr, 10),
        isUntagged: false,
        isArrears: false,
        isDischarged: false,
        isReferrerDeduction: false,
        isManualAdjustment: true,
        manualAdjustmentId: recipientRow.manualAdjustmentId,
      });
    }

    return rows;
  }, [recipientBqRows, recipientManualRows, rawLoanTags, tagMap, currentMonth]);

  // Reconciliation — computed from loan tags broker share + salary config history
  const reconciliation: MonthlyReconciliation[] = useMemo(() => {
    if (loanTags.length === 0) return [];

    // Sum broker share per month from loan tags (source of truth after split %)
    const monthMap = new Map<string, number>();
    for (const tag of loanTags) {
      const m = tag.month.slice(0, 7); // 'YYYY-MM'
      monthMap.set(m, (monthMap.get(m) || 0) + tag.brokerShareCents);
    }
    const brokerMonthlyComms = Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, cents]) => ({ month, cents }));

    if (brokerMonthlyComms.length === 0) return [];

    // Build salary history from config history
    const salaryHistory = rawConfigHistory
      .filter(c => c.baseSalaryCents != null)
      .map(c => ({
        effectiveFrom: c.effectiveFrom,
        baseSalaryCents: c.baseSalaryCents!,
      }));

    return computeReconciliation(brokerMonthlyComms, salaryHistory);
  }, [loanTags, rawConfigHistory]);

  // Monthly revenue hurdle schedule on the same rows and the same share basis
  // as the reconciliation above. Empty for brokers without a hurdle.
  const hurdleSchedule = useMemo(() => {
    if (loanTags.length === 0 || !hasRevenueHurdle(rawConfigHistory)) return [];
    return computeHurdleSchedule(loanTags, rawConfigHistory, { shareOf: l => l.brokerShareCents });
  }, [loanTags, rawConfigHistory]);

  // Group reconciliation by FINANCIAL year (descending) for collapsible display.
  // Was calendar year, which put an unlabelled "2026" one line under an
  // "FY27 to date" tile and made the two look like they should agree.
  const reconYearGroups = useMemo(() => groupByFiscalYear(reconciliation), [reconciliation]);

  // Newest period month with data, so "FY to date" can say what it covers.
  const latestLoadedMonth = useMemo(() => latestMonth(loanTags), [loanTags]);

  // No saved split: the server prices rows at a default nobody agreed. Show
  // "Not configured" rather than money at an invented rate.
  const notConfigured = !currentConfig;

  const untaggedCount = useMemo(() => loanTags.filter(l => l.isUntagged).length, [loanTags]);

  // Reconciliation lookup by month key for pay summary in loan tags
  const reconByMonth = useMemo(() => {
    const map = new Map<string, MonthlyReconciliation>();
    for (const r of reconciliation) {
      map.set(r.month, r);
    }
    return map;
  }, [reconciliation]);

  // Year > Month > Loans tree (with sorting — Issue 5)
  const yearGroups = useMemo(() => {
    let filtered = [...loanTags];
    if (tagFilter === 'untagged') filtered = filtered.filter(l => l.isUntagged);
    else if (tagFilter === 'broker') filtered = filtered.filter(l => l.introductionType === 'broker');
    else if (tagFilter === 'company') filtered = filtered.filter(l => l.introductionType === 'company');
    else if (tagFilter === 'self_sourced') filtered = filtered.filter(l => l.introductionType === 'self_sourced');
    else if (tagFilter === 'arrears') filtered = filtered.filter(l => l.isArrears);
    else if (tagFilter === 'discharged') filtered = filtered.filter(l => l.isDischarged);
    if (tagSearch) {
      const q = tagSearch.toLowerCase();
      filtered = filtered.filter(l => l.loanId.toLowerCase().includes(q) || l.customerName.toLowerCase().includes(q));
    }
    // EVERY payment row renders. From 2026-03-27 this collapsed rows to the
    // latest month per loanId::paymentType on the premise that Loan Market
    // "carries forward" upfront records; the server already returns one row
    // per (loan, period_month, payment_type), so the collapse hid every
    // earlier trail month, every subsequent-upfront top-up and every clawback
    // that was not the latest row, while the tiles and reconciliation above
    // kept them all. Sam Landis, calendar 2026: $115,782.50 here against
    // $142,343.14 above. See utils/commissionTree.ts.
    const dir = loanSort.dir === 'asc' ? 1 : -1;
    const sortLoans = (a: LoanTagRow, b: LoanTagRow): number => {
      switch (loanSort.col) {
        case 'customer': return dir * a.customerName.localeCompare(b.customerName);
        case 'lender': return dir * (a.lender || '').localeCompare(b.lender || '');
        case 'type': return dir * (a.paymentType || '').localeCompare(b.paymentType || '');
        case 'commission': return dir * (a.commissionCents - b.commissionCents);
        case 'loanValue': return dir * ((a.loanValueCents ?? -1) - (b.loanValueCents ?? -1));
        case 'share': return dir * (a.brokerShareCents - b.brokerShareCents);
        default: return dir * a.loanId.localeCompare(b.loanId);
      }
    };
    return buildFiscalYearGroups(filtered, {
      sortLoans,
      grossVisible: l => l.grossCommissionCents !== undefined,
    });
  }, [loanTags, tagFilter, tagSearch, loanSort]);

  const treeIsFiltered = tagFilter !== 'all' || tagSearch.trim() !== '';

  // FY-to-date totals, with how many months are loaded so the tile can say
  // "1 month loaded" instead of implying a full year to date.
  const reconYtdTotals = useMemo(() => sumFiscalYear(reconciliation, currentFY), [reconciliation, currentFY]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const toggleYear = (y: number) => setExpandedYears(p => { const n = new Set(p); n.has(y) ? n.delete(y) : n.add(y); return n; });
  const toggleMonth = (m: string) => setExpandedMonths(p => { const n = new Set(p); n.has(m) ? n.delete(m) : n.add(m); return n; });
  const expandAll = () => { setExpandedYears(new Set(yearGroups.map(y => y.year))); setExpandedMonths(new Set(yearGroups.flatMap(y => y.months.map(m => m.month)))); };
  const collapseAll = () => { setExpandedYears(new Set([yearGroups[0]?.year].filter(Boolean) as number[])); setExpandedMonths(new Set()); };
  const toggleLoanSelection = (id: string) => setSelectedLoanIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleTagLoan = useCallback(async (loanId: string, introductionType: 'broker' | 'company' | 'self_sourced', lockedPct: number, customerName?: string, lender?: string, lockedTrailPct?: number) => {
    if (!companyId || !brokerDocId) return;
    try {
      const tagFn = httpsCallable(functions, 'tagLoanIntroductionType');
      await tagFn({
        companyId,
        brokerDocId,
        loanId,
        introductionType,
        lockedPct,
        customerName: customerName || null,
        lender: lender || null,
        // PR C — lockedTrailPct is required by backend for self_sourced and
        // rejected for broker/company. Only forward when explicitly given.
        ...(introductionType === 'self_sourced' && lockedTrailPct !== undefined && { lockedTrailPct }),
      });
      await refetchBigQueryData();
      toast.success(`Loan ${loanId} tagged as ${introductionType} introduction (${lockedPct}%)`);
    } catch (err: any) {
      console.error('[BrokerCommissionView] Tag error:', err);
      toast.error(err?.message || 'Failed to tag loan');
    }
  }, [companyId, brokerDocId, refetchBigQueryData]);

  const handleBulkTag = useCallback(async (introductionType: 'broker' | 'company' | 'self_sourced') => {
    if (!companyId || !brokerDocId || selectedLoanIds.size === 0) return;
    const pctToLock =
      introductionType === 'broker' ? brokerIntroPct
      : introductionType === 'company' ? companyIntroPct
      : upfrontSelfSourcedPct;
    const trailPctToLock = introductionType === 'self_sourced' ? trailSelfSourcedPct : undefined;
    try {
      const bulkTagFn = httpsCallable(functions, 'bulkTagLoanIntroductionType');
      await bulkTagFn({
        companyId,
        brokerDocId,
        loanIds: Array.from(selectedLoanIds),
        introductionType,
        lockedPct: pctToLock,
        ...(trailPctToLock !== undefined && { lockedTrailPct: trailPctToLock }),
      });
      await refetchBigQueryData();
      toast.success(`${selectedLoanIds.size} loans tagged as ${introductionType} introduction (${pctToLock}%)`);
      setSelectedLoanIds(new Set());
    } catch (err: any) {
      console.error('[BrokerCommissionView] Bulk tag error:', err);
      toast.error(err?.message || 'Failed to bulk tag loans');
    }
  }, [companyId, brokerDocId, selectedLoanIds, brokerIntroPct, companyIntroPct, upfrontSelfSourcedPct, trailSelfSourcedPct, refetchBigQueryData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchBigQueryData();
    } finally {
      setRefreshing(false);
    }
  };

  const isLoading = configLoading || recipientViewLoading;
  const showGrossColumn = shouldShowRecipientGrossColumn(recipientRows);

  if (!companyId || companyData?.featureFlags?.commission_settings !== true) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-gray-500">This feature is not yet available for your company.</p>
      </div>
    );
  }

  // A broker-family user with no own broker doc (e.g. a credit analyst who only
  // services linked brokers) has no personal commission book. Show a clear empty
  // state instead of the page's default 60/40 placeholders, which read as a real
  // split. Their linked brokers' loans and clients appear under Loans / Portfolio;
  // they earn no upfront or trail commission on a linked book.
  if (!brokerDocId) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] px-4">
        <div className="text-center max-w-md">
          <p className="text-gray-300 font-medium mb-1">You have no personal commission book.</p>
          <p className="text-gray-500 text-sm">
            Your account is linked to other brokers for servicing only. Their loans and clients appear under Loans and Portfolio. You do not earn upfront or trail commission on a linked book.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={UserCog}
        title="My Commission Settings"
        subtitle={`${userData?.displayName || 'Broker'} — Read-Only View`}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
      />

      {isLoading && <LoadingState message="Loading your commission settings..." />}

      {!isLoading && recipientViewError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-5 text-red-300">
          Commission payouts could not be loaded. No financial amounts are displayed.{' '}
          {recipientViewError}
        </div>
      )}

      {!isLoading && !recipientViewError && recipientView && (
        <>
          {/* Trail Start Date Info */}
          {trailStartMonth && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-500/[0.08] border border-blue-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400 flex-shrink-0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span className="text-sm text-blue-400">
                <strong>Trail commissions visible from:</strong> {(() => { const [y, m] = trailStartMonth.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${months[parseInt(m,10)-1]} ${y}`; })()} onward. Contact your administrator to update this setting.
              </span>
            </div>
          )}

          {/* Data currency. Every figure on this page is valued at the current
              saved split and stops at the newest period month loaded. */}
          <p className="text-xs text-gray-400">
            {latestLoadedMonth
              ? <>Commission data current to <span className="text-gray-200 font-semibold">{monthLabel(latestLoadedMonth)}</span></>
              : 'No commission data loaded yet'}
            {' · '}your share valued at the current saved split
            {tagsHasMore && <span className="text-amber-400"> · loading loan tags…</span>}
          </p>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCardWithIcon label="My Commission (FY to date)" value={notConfigured ? 'Not configured' : reconYtdTotals.commsCents ? formatCurrencyWithCents(reconYtdTotals.commsCents) : '$0.00'} accent="blue" detail={notConfigured ? 'No commission split has been saved for you yet' : fiscalYearToDateDetail(currentFY, reconYtdTotals)} valueColor={notConfigured ? 'text-gray-400' : undefined} icon={<DollarSign className="w-5 h-5" />} iconBg="rgba(59,130,246,0.3)" iconColor="#60a5fa" />
            <StatCardWithIcon label="Base Salary (Monthly)" value={currentConfig?.baseSalaryCents ? formatCurrencyWithCents(currentConfig.baseSalaryCents) : '$0.00'} accent="green" detail={currentConfig ? `Config saved ${formatDateShort(currentConfig.effectiveFrom)}` : 'Not configured'} icon={<CreditCard className="w-5 h-5" />} iconBg="rgba(34,197,94,0.3)" iconColor="#4ade80" />
            <StatCardWithIcon label="Carry Over Balance" value={notConfigured ? 'Not configured' : reconYtdTotals.carryOverCents ? formatCurrencyWithCents(reconYtdTotals.carryOverCents) : '$0.00'} accent="purple" detail={notConfigured ? 'No split saved' : reconYtdTotals.carryOverCents < 0 ? 'Shortfall carried to next month' : 'No carry over'} valueColor={notConfigured ? 'text-gray-400' : reconYtdTotals.carryOverCents < 0 ? 'text-red-400' : undefined} icon={<DollarSign className="w-5 h-5" />} iconBg="rgba(139,92,246,0.3)" iconColor="#a78bfa" />
            <StatCardWithIcon label="Untagged Loans" value={tagsHasMore ? '…' : String(untaggedCount)} accent="amber" detail={tagsHasMore ? 'Loading all loan tags' : `Defaulting to Company Intro (${companyIntroPct}%)`} valueColor="text-amber-400" icon={<Tag className="w-5 h-5" />} iconBg="rgba(245,158,11,0.3)" iconColor="#fbbf24" />
          </div>

          {/* Commission vs Base Salary Reconciliation — Issue 1 & 2: Year-grouped, descending */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white">Commission vs Base Salary Reconciliation</h3>
                  <p className="text-sm text-gray-400 mt-1">Monthly draw against commission — base salary is the guaranteed minimum. Shortfalls carry over.</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Legend:</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-green-500/10 text-green-400">Excess = paid on top</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-red-500/10 text-red-400">Shortfall = carry over</span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Month</th>
                    {['Broker Comms', 'Base Salary', 'Total Paid', 'Amt On Top of Base', 'Carry Over'].map(h => <th key={h} className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">{h}</th>)}
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">No reconciliation data available yet. Data will appear once commissions are uploaded.</td></tr>
                  )}
                  {notConfigured && reconciliation.length > 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500 italic">Not configured. No commission split has been saved for you yet, so there is nothing to reconcile.</td></tr>
                  )}
                  {!notConfigured && reconYearGroups.map(yg => {
                    const yearTotal = yg.months.reduce((s, r) => s + r.brokerCommsCents, 0);
                    const isExpanded = expandedReconYears.has(yg.year);
                    return (
                      <React.Fragment key={yg.year}>
                        <tr className="bg-gray-700/30 border-b border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors" onClick={() => setExpandedReconYears(prev => { const next = new Set(prev); if (next.has(yg.year)) next.delete(yg.year); else next.add(yg.year); return next; })}>
                          <td className="px-6 py-3 text-white font-bold"><span className="text-gray-500 mr-2">{isExpanded ? '\u25BE' : '\u25B8'}</span>{yg.label}<span className="ml-2 text-xs font-normal text-gray-500">{yg.rangeLabel}</span></td>
                          <td className="px-6 py-3 text-right text-green-400 font-bold">{notConfigured ? <span className="text-gray-500 italic font-normal">Not configured</span> : formatCurrencyWithCents(yearTotal)}</td>
                          <td className="px-6 py-3 text-right text-gray-500 text-xs">{yg.months.length} month{yg.months.length === 1 ? '' : 's'} loaded</td>
                          <td colSpan={4}></td>
                        </tr>
                        {isExpanded && yg.months.map(r => (
                          <tr key={r.month} className="border-b border-gray-800 hover:bg-vlt-dark transition-colors">
                            <td className="px-6 py-3.5 pl-10 text-white font-medium">{formatMonthLabel(r.month)}</td>
                            <td className={`px-6 py-3.5 text-right font-semibold ${r.brokerCommsCents >= r.baseSalaryCents ? 'text-green-400' : 'text-amber-400'}`}>{formatCurrencyWithCents(r.brokerCommsCents)}</td>
                            <td className="px-6 py-3.5 text-right text-gray-300">{formatCurrencyWithCents(r.baseSalaryCents)}</td>
                            <td className="px-6 py-3.5 text-right text-white font-bold">{formatCurrencyWithCents(r.totalPaidCents)}</td>
                            <td className={`px-6 py-3.5 text-right font-bold ${r.amtOnTopOfBaseCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrencyWithCents(r.amtOnTopOfBaseCents)}</td>
                            <td className={`px-6 py-3.5 text-right font-bold ${r.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{r.carryOverCents === 0 ? '$0.00' : formatCurrencyWithCents(r.carryOverCents)}</td>
                            <td className={`px-6 py-3.5 text-xs ${r.note.includes('Shortfall') ? 'text-red-400' : 'text-gray-500'}`}>{r.note}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {reconciliation.length > 0 && !notConfigured && (
                    <tr className="bg-blue-500/5 border-t border-gray-600">
                      <td className="px-6 py-4 text-white font-bold">{fiscalYearLabel(currentFY)} to date<span className="ml-2 text-xs font-normal text-gray-500">{fiscalYearToDateDetail(currentFY, reconYtdTotals).replace(`${fiscalYearLabel(currentFY)} · `, '')}</span></td>
                      <td className="px-6 py-4 text-right text-green-400 font-bold text-lg">{notConfigured ? <span className="text-gray-500 italic text-sm font-normal">Not configured</span> : formatCurrencyWithCents(reconYtdTotals.commsCents)}</td>
                      <td className="px-6 py-4 text-right text-gray-300 font-bold">{formatCurrencyWithCents(reconYtdTotals.baseSalaryCents)}</td>
                      <td className="px-6 py-4 text-right text-white font-bold text-lg">{formatCurrencyWithCents(reconYtdTotals.totalPaidCents)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${reconYtdTotals.amtOnTopCents >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrencyWithCents(reconYtdTotals.amtOnTopCents)}</td>
                      <td className={`px-6 py-4 text-right font-bold ${reconYtdTotals.carryOverCents < 0 ? 'text-red-400' : 'text-gray-500'}`}>{formatCurrencyWithCents(reconYtdTotals.carryOverCents)}</td>
                      <td className="px-6 py-4 text-xs text-gray-400">Net amount to be paid on top of base</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {hurdleSchedule.length > 0 && (
            <RevenueHurdleSchedule schedule={hurdleSchedule} shareBasisLabel="your share" />
          )}

          {/* Commission Split Percentages (read-only) — 3-tier or legacy 2-tier */}
          {threeTierActive ? (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Client Group Commission Tiers</h3>
                <p className="text-sm text-gray-400 mt-1">Per-tier upfront and trail split based on who sourced the client</p>
              </div>
              <div className="p-6 space-y-5">
                {([
                  { key: 'company', label: 'Internal Referral', sub: 'Company introduced the client to you',
                    Icon: Building2, color: 'purple', upfront: companyIntroPct, trail: trailCompanyIntroPct },
                  { key: 'broker', label: 'Repeat / Existing Client', sub: 'You re-introduced a known client',
                    Icon: User, color: 'blue', upfront: brokerIntroPct, trail: trailBrokerIntroPct },
                  { key: 'self_sourced', label: 'Self-Sourced Business', sub: 'You independently sourced the client',
                    Icon: Briefcase, color: 'emerald', upfront: upfrontSelfSourcedPct, trail: trailSelfSourcedPct },
                ] as const).map(({ key, label, sub, Icon, color, upfront, trail }) => (
                  <div key={key} className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center">
                    <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 text-${color}-400`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold w-14 text-right">Upfront</span>
                      <span className="w-16 text-center text-base font-bold text-white bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5">{upfront}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-cyan-400 font-semibold w-14 text-right">Trail</span>
                      <span className="w-16 text-center text-base font-bold text-white bg-gray-700 border border-gray-600 rounded-lg px-2 py-1.5">{trail}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
              <div className="p-6 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white">Commission Split Percentages</h3>
                <p className="text-sm text-gray-400 mt-1">Your commission share based on who introduced the customer</p>
              </div>
              <div className="p-6 space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center"><User className="w-4 h-4 text-blue-400" /></div><div><label className="text-sm font-semibold text-white">Broker Introduction</label><p className="text-xs text-gray-500">When you introduced the customer to the company</p></div></div>
                    <span className="text-2xl font-bold text-white">{brokerIntroPct}%</span>
                  </div>
                  <div className="flex items-center gap-4"><div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-blue-400" style={{ width: `${brokerIntroPct}%` }} /></div><div className="flex items-center gap-2 text-xs w-64 justify-end"><span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 font-semibold">You {brokerIntroPct}%</span><span className="text-gray-600">|</span><span className="px-2 py-0.5 rounded bg-gray-600/50 text-gray-400 font-semibold">Company {100 - brokerIntroPct}%</span></div></div>
                </div>
                <div className="h-px bg-gray-700" />
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center"><Building2 className="w-4 h-4 text-purple-400" /></div><div><label className="text-sm font-semibold text-white">Company Introduction</label><p className="text-xs text-gray-500">When the company introduced the customer to you</p></div></div>
                    <span className="text-2xl font-bold text-white">{companyIntroPct}%</span>
                  </div>
                  <div className="flex items-center gap-4"><div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-400" style={{ width: `${companyIntroPct}%` }} /></div><div className="flex items-center gap-2 text-xs w-64 justify-end"><span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 font-semibold">You {companyIntroPct}%</span><span className="text-gray-600">|</span><span className="px-2 py-0.5 rounded bg-gray-600/50 text-gray-400 font-semibold">Company {100 - companyIntroPct}%</span></div></div>
                </div>
              </div>
            </div>
          )}

          {/* Base Salary (read-only) */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg">
            <div className="p-6 border-b border-gray-700"><h3 className="text-lg font-bold text-white">Base Salary</h3><p className="text-sm text-gray-400 mt-1">Your monthly base salary</p></div>
            <div className="p-6">
              {currentConfig?.baseSalaryCents ? (
                <div className="flex items-center justify-between p-4 rounded-lg bg-vlt-dark border border-blue-500/30">
                  <div className="flex items-center gap-3"><span className="w-2 h-2 rounded-full bg-blue-400" /><span className="text-lg text-white font-bold">{formatCurrencyWithCents(currentConfig.baseSalaryCents)} / month</span></div>
                  <span className="text-sm text-blue-400 font-medium">Effective {formatDateShort(currentConfig.effectiveFrom)}</span>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No base salary configured.</p>
              )}
            </div>
          </div>

          {/* Loan Commission Tags — Broker can TAG and bulk-select */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg flex flex-col">
            <div className="p-6 border-b border-gray-700 shrink-0">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><h3 className="text-lg font-bold text-white">My Loan Commission Tags</h3><p className="text-sm text-gray-400 mt-1">Every commission payment, grouped by financial year and month. Tag each loan with how the customer was introduced; the tag applies to every payment on that loan.{treeIsFiltered && <span className="ml-2 px-1.5 py-0.5 text-[0.625rem] font-semibold rounded bg-blue-500/20 text-blue-300 border border-blue-500/30 uppercase tracking-wider">Filtered: subtotals cover matching payments only</span>}</p></div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button disabled={selectedLoanIds.size === 0} onClick={() => handleBulkTag('broker')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Tag className="w-3.5 h-3.5" />Broker ({selectedLoanIds.size})</button>
                  <button disabled={selectedLoanIds.size === 0} onClick={() => handleBulkTag('company')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Tag className="w-3.5 h-3.5" />Company ({selectedLoanIds.size})</button>
                  {threeTierActive && (
                    <button disabled={selectedLoanIds.size === 0} onClick={() => handleBulkTag('self_sourced')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Tag className="w-3.5 h-3.5" />Self-Sourced ({selectedLoanIds.size})</button>
                  )}
                  <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} className="bg-gray-700 border border-gray-600 text-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="all">All payments ({loanTags.length})</option><option value="untagged">Untagged ({loanTags.filter(l => l.isUntagged).length})</option><option value="broker">Broker Introduction</option><option value="company">Company Introduction</option>{threeTierActive && <option value="self_sourced">Self-Sourced ({loanTags.filter(l => l.introductionType === 'self_sourced').length})</option>}
                  </select>
                  <div className="relative"><Search className="w-3.5 h-3.5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" /><input type="text" value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search loan ID or customer..." className="bg-gray-700 border border-gray-600 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 w-56" /></div>
                  <button onClick={expandAll} className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors">Expand All</button>
                  <button onClick={collapseAll} className="px-3 py-2 text-xs font-medium rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors">Collapse All</button>
                </div>
              </div>
            </div>
            {tagsLoading && <LoadingState message="Loading your loan tags..." />}
            {!tagsLoading && (
              <div className="overflow-auto flex-1 min-h-0 max-h-[70vh]">
                <table className="w-full">
                  {/* Issue 3: Sticky thead + Issue 5: Sortable columns */}
                  <thead className="bg-gray-900 border-b border-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-4 text-left w-10"><input type="checkbox" className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" title="Select all" onChange={() => { setSelectedLoanIds(prev => { const allTaggable = yearGroups.flatMap(yg => yg.months.flatMap(mg => mg.loans)).filter(isTaggableRow); if (allTaggable.every(l => prev.has(l.loanId))) return new Set(); const next = new Set(prev); allTaggable.forEach(l => next.add(l.loanId)); return next; }); }} checked={(() => { const allTaggable = yearGroups.flatMap(yg => yg.months.flatMap(mg => mg.loans)).filter(isTaggableRow); return allTaggable.length > 0 && allTaggable.every(l => selectedLoanIds.has(l.loanId)); })()} /></th>
                      {([['loanId', 'Period / Loan ID', 'left'], ['customer', 'Customer', 'left'], ['lender', 'Lender', 'left'], ['loanValue', 'Loan Amount', 'right'], ['type', 'Type', 'left']] as const).map(([col, label, align]) => (
                        <th key={col} className={`px-4 py-4 text-${align} text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors`} onClick={() => setLoanSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                          {label}{loanSort.col === col && <span className="ml-1">{loanSort.dir === 'asc' ? '\u2191' : '\u2193'}</span>}
                        </th>
                      ))}
                      {showGrossColumn && (
                        <th className="px-4 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors" onClick={() => setLoanSort(prev => ({ col: 'commission', dir: prev.col === 'commission' && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                          <span title="Commission paid by the aggregator, before your split">Gross Commission</span>{loanSort.col === 'commission' && <span className="ml-1">{loanSort.dir === 'asc' ? '\u2191' : '\u2193'}</span>}
                        </th>
                      )}
                      <th className="px-4 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">Introduction</th>
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">Effective Rate</th>
                      <th className="px-4 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors" onClick={() => setLoanSort(prev => ({ col: 'share', dir: prev.col === 'share' && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
                        Broker Share{loanSort.col === 'share' && <span className="ml-1">{loanSort.dir === 'asc' ? '\u2191' : '\u2193'}</span>}
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearGroups.length === 0 && (
                      <tr><td colSpan={showGrossColumn ? 11 : 10} className="px-4 py-8 text-center text-gray-500">No loan tags found. Tags will appear as you tag your loans.</td></tr>
                    )}
                    {yearGroups.map(yg => (
                      <React.Fragment key={yg.year}>
                        {/* Year row with checkbox — Issue 4 */}
                        <tr className="bg-gray-700/30 border-b border-gray-600 cursor-pointer" onClick={() => toggleYear(yg.year)}>
                          <td className="px-4 py-3"><input type="checkbox" checked={yg.months.flatMap(m => m.loans).filter(isTaggableRow).length > 0 && yg.months.flatMap(m => m.loans).filter(isTaggableRow).every(l => selectedLoanIds.has(l.loanId))} onChange={(e) => { e.stopPropagation(); setSelectedLoanIds(prev => { const next = new Set(prev); const taggable = yg.months.flatMap(m => m.loans).filter(isTaggableRow); if (taggable.every(l => next.has(l.loanId))) { taggable.forEach(l => next.delete(l.loanId)); } else { taggable.forEach(l => next.add(l.loanId)); } return next; }); }} onClick={e => e.stopPropagation()} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" /></td>
                          <td className="px-4 py-3 text-white font-bold text-base" colSpan={2}><span className="inline-flex items-center gap-2"><ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${expandedYears.has(yg.year) ? 'rotate-90' : ''}`} />{yg.label}<span className="text-xs font-normal text-gray-500">{yg.rangeLabel}</span></span></td>
                          <td className="px-4 py-3 text-gray-500 text-sm">{yg.months.length} month{yg.months.length === 1 ? '' : 's'}</td><td className="px-4 py-3" /><td className="px-4 py-3" />
                          {showGrossColumn && <td className="px-4 py-3 text-right text-green-400 font-bold">{formatCurrencyWithCents(yg.totalCommsCents)}</td>}<td className="px-4 py-3" /><td className="px-4 py-3" />
                          <td className="px-4 py-3 text-right text-green-400 font-bold">{notConfigured ? <span className="text-gray-500 italic font-normal text-sm">Not configured</span> : formatCurrencyWithCents(yg.totalBrokerShareCents)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{yg.paymentCount} payments · {yg.loanCount} loans</td>
                        </tr>
                        {expandedYears.has(yg.year) && yg.months.map(mg => (
                          <React.Fragment key={mg.month}>
                            {/* Month row with checkbox — Issue 4 */}
                            <tr className="bg-vlt-dark/40 border-b border-gray-800 cursor-pointer hover:bg-vlt-dark/70 transition-colors" onClick={() => toggleMonth(mg.month)}>
                              <td className="px-4 py-3 pl-8"><input type="checkbox" checked={mg.loans.filter(isTaggableRow).length > 0 && mg.loans.filter(isTaggableRow).every(l => selectedLoanIds.has(l.loanId))} onChange={(e) => { e.stopPropagation(); setSelectedLoanIds(prev => { const next = new Set(prev); const taggable = mg.loans.filter(isTaggableRow); if (taggable.every(l => next.has(l.loanId))) { taggable.forEach(l => next.delete(l.loanId)); } else { taggable.forEach(l => next.add(l.loanId)); } return next; }); }} onClick={e => e.stopPropagation()} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" /></td>
                              <td className="px-4 py-3 text-cyan-400 font-semibold" colSpan={2}><span className="inline-flex items-center gap-2"><ChevronRight className={`w-3 h-3 transition-transform duration-200 ${expandedMonths.has(mg.month) ? 'rotate-90' : ''}`} />{MONTH_NAMES[parseInt(mg.month.split('-')[1], 10) - 1]}</span></td>
                              <td className="px-4 py-3 text-gray-500 text-sm whitespace-nowrap">{mg.paymentCount} payments · {mg.loanCount} loans</td><td className="px-4 py-3" /><td className="px-4 py-3" />
                              {showGrossColumn && <td className="px-4 py-3 text-right text-green-400 font-semibold">{formatCurrencyWithCents(mg.totalCommsCents)}</td>}<td className="px-4 py-3" /><td className="px-4 py-3" />
                              <td className="px-4 py-3 text-right text-green-400 font-semibold">{notConfigured ? <span className="text-gray-500 italic font-normal text-sm">Not configured</span> : formatCurrencyWithCents(mg.totalBrokerShareCents)}</td>
                              <td className="px-4 py-3"><div className="flex items-center gap-1.5 flex-nowrap">{mg.referrerDeductionCount > 0 && <span className="whitespace-nowrap px-1.5 py-0.5 text-[0.625rem] rounded bg-violet-500/20 text-violet-400 font-semibold" title="Third-party referrer fees on this month's loans. Paid by the company, not deducted from you.">{mg.referrerDeductionCount} referrer</span>}{mg.untaggedCount > 0 && <span className="whitespace-nowrap px-1.5 py-0.5 text-[0.625rem] rounded bg-amber-500/20 text-amber-400 font-semibold">{mg.untaggedCount} untagged</span>}{mg.arrearsCount > 0 && <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">ARREARS</span>}{mg.dischargedCount > 0 && <span className="whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30">{mg.dischargedCount} discharged</span>}</div></td>
                            </tr>
                            {expandedMonths.has(mg.month) && (() => {
                              const recon = reconByMonth.get(mg.month);
                              if (!recon || recon.baseSalaryCents <= 0) return null;
                              const amtOnTop = recon.amtOnTopOfBaseCents;
                              const carryOver = recon.carryOverCents;
                              return (
                                <tr className="border-b border-gray-700/50">
                                  <td colSpan={showGrossColumn ? 11 : 10} className="px-4 py-3 pl-8">
                                    <div className="flex items-center gap-2">
                                      <div className="grid grid-cols-4 gap-px rounded-lg overflow-hidden border border-gray-600/50 text-sm w-fit">
                                        <div className="bg-gray-700/60 px-5 py-2.5 text-center">
                                          <div className="text-gray-400 font-medium text-xs mb-1">Broker Share</div>
                                          <div className="text-white font-bold">{formatCurrencyWithCents(recon.brokerCommsCents)}</div>
                                        </div>
                                        <div className="bg-gray-700/60 px-5 py-2.5 text-center">
                                          <div className="text-gray-400 font-medium text-xs mb-1">Base Salary</div>
                                          <div className="text-gray-300 font-bold">{formatCurrencyWithCents(recon.baseSalaryCents)}</div>
                                        </div>
                                        <div className={`px-5 py-2.5 text-center ${amtOnTop >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                                          <div className="text-gray-400 font-medium text-xs mb-1">Amt On Top of Base</div>
                                          <div className={`font-bold ${amtOnTop >= 0 ? 'text-green-400' : 'text-red-400'}`}>{amtOnTop < 0 ? `-${formatCurrencyWithCents(Math.abs(amtOnTop))}` : formatCurrencyWithCents(amtOnTop)}</div>
                                        </div>
                                        <div className={`px-5 py-2.5 text-center ${carryOver < 0 ? 'bg-red-500/10' : 'bg-gray-700/60'}`}>
                                          <div className="text-gray-400 font-medium text-xs mb-1">Carry Over</div>
                                          <div className={`font-bold ${carryOver < 0 ? 'text-red-400' : 'text-gray-500'}`}>{carryOver < 0 ? `-${formatCurrencyWithCents(Math.abs(carryOver))}` : '$0.00'}</div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })()}
                            {expandedMonths.has(mg.month) && mg.loans.map(loan => (
                              <tr key={paymentRowKey(loan)} className={`border-b border-gray-800/50 transition-colors hover:bg-vlt-dark/50 ${loan.isManualAdjustment ? 'bg-indigo-500/5' : loan.isUntagged ? 'bg-amber-500/5' : ''}`}>
                                {/* Loan-level checkbox — only for taggable loans (not manual/arrears/discharged) */}
                                <td className="px-4 py-3 pl-14">
                                  {isTaggableRow(loan) ? (
                                    <input type="checkbox" checked={selectedLoanIds.has(loan.loanId)} onChange={() => toggleLoanSelection(loan.loanId)} className="w-4 h-4 rounded border-2 border-gray-500 bg-gray-700 text-blue-600" />
                                  ) : <span className="w-4 h-4 inline-block" />}
                                </td>
                                <td className="px-4 py-3 text-blue-400 font-mono text-sm">{loan.loanId}</td>
                                <td className="px-4 py-3 text-white text-sm">{loan.customerName}</td>
                                <td className="px-4 py-3 text-gray-400 text-sm">{loan.lender}</td>
                                {/* Settled amount on upfront, current balance on trail. Null
                                    renders as an em dash - never $0, which would read as a
                                    real zero-value loan. */}
                                <td className="px-4 py-3 text-right text-gray-300 text-sm tabular-nums" title={loan.loanValueCents == null ? undefined : loan.paymentType === 'upfront' ? 'Settled loan amount for this commission event' : 'Loan balance at this period, or the settled amount where the lender did not report a balance'}>{loan.loanValueCents == null ? <span className="text-gray-600">—</span> : formatCurrency(loan.loanValueCents)}</td>
                                <td className="px-4 py-3"><PaymentTypeBadge type={loan.paymentType} /></td>
                                {showGrossColumn && (
                                  <td className={`px-4 py-3 text-right font-semibold ${loan.commissionCents < 0 ? 'text-rose-400' : 'text-white'}`}>
                                    {formatCurrencyWithCents(loan.grossCommissionCents!)}
                                  </td>
                                )}
                                <td className="px-4 py-3 text-center">
                                  {loan.isArrears || loan.isDischarged || loan.isReferrerDeduction ? (
                                    <span className={`px-2 py-1 text-xs rounded-md ${loan.isDischarged ? 'text-rose-400' : 'text-amber-400'}`}>—</span>
                                  ) : (() => {
                                    const displayPct = loan.lockedPct ?? (
                                      loan.introductionType === 'broker' ? brokerIntroPct
                                      : loan.introductionType === 'self_sourced' ? upfrontSelfSourcedPct
                                      : companyIntroPct
                                    );
                                    const selectClass = loan.isUntagged
                                      ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300'
                                      : loan.introductionType === 'broker'
                                        ? 'bg-blue-500/20 border border-blue-500/40 text-blue-300'
                                        : loan.introductionType === 'self_sourced'
                                          ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
                                          : 'bg-purple-500/20 border border-purple-500/40 text-purple-300';
                                    const pctClass = loan.introductionType === 'broker'
                                      ? 'bg-green-500/20 border border-green-500/40 text-green-300'
                                      : loan.introductionType === 'self_sourced'
                                        ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
                                        : 'bg-red-500/20 border border-red-500/40 text-red-300';
                                    return (
                                    <div className="flex items-center justify-center gap-1.5">
                                      <select
                                        value={loan.introductionType ?? ''}
                                        onChange={e => {
                                          const val = e.target.value as 'broker' | 'company' | 'self_sourced';
                                          if (val) {
                                            // PR C — self_sourced uses the upfront pct from config; the
                                            // backend captures lockedTrailPct alongside for trail rows.
                                            const pctToLock =
                                              val === 'broker' ? brokerIntroPct
                                              : val === 'self_sourced' ? upfrontSelfSourcedPct
                                              : companyIntroPct;
                                            const trailPctToLock = val === 'self_sourced' ? trailSelfSourcedPct : undefined;
                                            handleTagLoan(loan.loanId, val, pctToLock, loan.customerName, loan.lender, trailPctToLock);
                                          }
                                        }}
                                        className={`rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${selectClass}`}
                                      >
                                        {loan.isUntagged && <option value="" disabled>Select Introduction</option>}
                                        <option value="broker">Broker ({brokerIntroPct}%)</option>
                                        <option value="company">Company ({companyIntroPct}%)</option>
                                        {threeTierActive && <option value="self_sourced">Self-Sourced ({upfrontSelfSourcedPct}/{trailSelfSourcedPct}%)</option>}
                                      </select>
                                      {!loan.isUntagged && (
                                        <span className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${pctClass}`}>
                                          {displayPct}%
                                        </span>
                                      )}
                                    </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-3 text-right text-blue-300 font-medium">{loan.effectiveRateBps === null ? '—' : `${(loan.effectiveRateBps / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`}</td>
                                <td className={`px-4 py-3 text-right font-semibold ${loan.isManualAdjustment ? (loan.brokerShareCents < 0 ? 'text-rose-400' : 'text-green-400') : loan.isArrears || loan.isDischarged || loan.isReferrerDeduction ? 'text-gray-500' : loan.isUntagged ? 'text-amber-400 italic' : loan.brokerShareCents < 0 ? 'text-rose-400' : 'text-green-400'}`}>{notConfigured && !loan.isManualAdjustment ? <span className="text-gray-500 italic font-normal text-xs">Not configured</span> : loan.isManualAdjustment ? formatCurrencyWithCents(loan.brokerShareCents) : loan.isArrears || loan.isDischarged || loan.isReferrerDeduction ? '—' : loan.isUntagged ? `~${formatCurrencyWithCents(loan.brokerShareCents)}*` : formatCurrencyWithCents(loan.brokerShareCents)}</td>
                                <td className="px-4 py-3"><LoanStatusBadge loan={loan} /></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="p-4 border-t border-gray-700 flex items-center justify-between shrink-0">
              <p className="text-xs text-gray-500">* Untagged loans default to Company Introduction percentage ({companyIntroPct}%). Select the correct introduction type for each loan. Commission is before your split.</p>
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-400">{loanTags.length} payments · {distinctLoanCount(loanTags)} loans · {yearGroups.reduce((s, y) => s + y.months.length, 0)} months{treeIsFiltered && ` (${yearGroups.reduce((s, y) => s + y.paymentCount, 0)} payments shown)`}</p>
                {tagsHasMore && <span className="text-xs text-amber-400">Loading loan tags…</span>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const BrokerCommissionViewPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<any>();
  const { brokerDocId: claimsBrokerDocId } = useAuth();
  const role = userData?.role as string | undefined;

  if (companyData?.featureFlags?.commission_settings !== true) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-gray-500">This feature is not yet available for your company.</p>
      </div>
    );
  }

  if (role === 'broker_assistant' || role === 'credit_analyst') {
    return (
      <RecipientCommissionReadOnlyView
        companyId={userData?.companyId}
        brokerDocId={claimsBrokerDocId || userData?.brokerDocId || undefined}
        displayName={userData?.displayName || userData?.email}
      />
    );
  }

  return <BrokerSelfCommissionViewPage />;
};

export default BrokerCommissionViewPage;
